import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import Navbar from "../components/Navbar";
import toast from "react-hot-toast";
import {
  FiPlus,
  FiSearch,
  FiEye,
  FiEdit2,
  FiTrash2,
  FiPlay,
  FiGift,
  FiAward,
  FiUsers,
  FiRefreshCw,
  FiClock,
  FiCheckCircle,
} from "react-icons/fi";
import {
  fetchEventList,
  deleteEvent,
  publishEvent,
  finalizeEvent,
  updateEventStatus,
} from "../../utils/eventApi";
import ConfirmActionModal from "./components/ConfirmActionModal";
import UtcClock from "./components/UtcClock";
import { useStickyState } from "../../hooks/useStickyState";
import { useScrollRestoration } from "../../hooks/useScrollRestoration";

const STATUS_TABS = [
  { key: "all", label: "All Events" },
  { key: "draft", label: "Draft" },
  { key: "coming_soon", label: "Coming Soon" },
  { key: "running", label: "Running" },
  { key: "completed", label: "Completed" },
  { key: "cancelled", label: "Cancelled" },
];

export const getStatusBadgeClass = (status) => {
  switch (status) {
    case "running":
      return "bg-emerald-100 text-emerald-700 border-emerald-200";
    case "completed":
      return "bg-blue-100 text-blue-700 border-blue-200";
    case "coming_soon":
      // Its own colour: announced is not draft and not live, and reusing either
      // would make the one state users can see look like one they cannot.
      return "bg-violet-100 text-violet-700 border-violet-200";
    case "draft":
      return "bg-amber-100 text-amber-700 border-amber-200";
    case "cancelled":
      return "bg-rose-100 text-rose-700 border-rose-200";
    default:
      return "bg-gray-100 text-gray-600 border-gray-200";
  }
};

/**
 * A gap, in the units that actually matter at that size.
 *
 * Days and hours while there are days left; hours and minutes inside a day;
 * minutes in the last hour. Nobody needs "0d 0h 7m", and "3d 4h 12m" buries the
 * only two numbers being read.
 */
const formatGap = (ms) => {
  const minutes = Math.max(0, Math.floor(ms / 60000));
  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  const mins = minutes % 60;

  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m`;
};

/**
 * What the schedule is doing right now.
 *
 * The dates alone say when an event runs; this says what that means at this
 * moment — which is the thing an admin scanning the list is actually after.
 *
 * A running event past its end date is called out rather than left looking
 * healthy: it has stopped accruing but still needs finalizing before anyone is
 * paid, and nothing else on this screen would say so.
 */
const scheduleNote = (event, now) => {
  const start = new Date(event.start_date).getTime();
  const end = new Date(event.end_date).getTime();
  if (!Number.isFinite(start) || !Number.isFinite(end)) return null;

  if (event.status === "completed") {
    return { text: event.total_days ? `Ran ${event.total_days} days` : "Finished", tone: "muted" };
  }
  if (event.status === "cancelled") return { text: "Cancelled", tone: "muted" };
  if (event.status === "draft") {
    return { text: event.total_days ? `${event.total_days} days planned` : "Not published", tone: "muted" };
  }

  if (now < start) return { text: `Starts in ${formatGap(start - now)}`, tone: "info" };
  if (now < end) {
    const left = end - now;
    // Under a day left is worth looking different: it is the window in which
    // someone still has time to extend it.
    return { text: `Ends in ${formatGap(left)}`, tone: left < 86400000 ? "urgent" : "live" };
  }
  return { text: "Ended — needs finalizing", tone: "urgent" };
};

const NOTE_TONES = {
  live: "bg-emerald-50 text-emerald-700 border-emerald-200",
  info: "bg-violet-50 text-violet-700 border-violet-200",
  urgent: "bg-amber-50 text-amber-800 border-amber-200",
  muted: "bg-gray-50 text-gray-500 border-gray-200",
};

/**
 * The status, as a person would say it.
 *
 * The API sends `coming_soon`, and CSS `capitalize` renders that as
 * "Coming_soon" — the underscore is a storage detail, not a word.
 */

export const getStatusLabel = (status) =>
  String(status || "")
    .split("_")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ") || "—";

const EventsList = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useStickyState("all", "evt_statusFilter");
  const [search, setSearch] = useStickyState("", "evt_search");
  const [debouncedSearch, setDebouncedSearch] = useStickyState("", "evt_debouncedSearch");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: "",
    message: "",
    subMessage: "",
    confirmText: "",
    confirmColor: "blue",
    icon: null,
    action: null,
    loading: false,
  });

  const searchTimer = useRef(null);

  /**
   * One clock for every row.
   *
   * Thirty seconds is enough for a countdown shown in days, hours and
   * minutes, and one interval for the table beats one per row.
   */
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const tick = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(tick);
  }, []);

  /**
   * Header counters.
   *
   * Each number is the API's own `pagination.total` for that filter, asked for
   * with `limit: 1` — the rows are thrown away, only the count is wanted. The
   * alternative, counting the page currently on screen, would report "3 events"
   * whenever the list was filtered or paged, which is worse than no number.
   *
   * Participants are deliberately absent: there is no endpoint that totals them
   * across events, and summing one page would be a number that quietly changes
   * as you page through.
   */
  const [stats, setStats] = useState({ total: null, running: null, coming_soon: null, completed: null, draft: null });

  const loadStats = useCallback(async () => {
    const countOf = async (status) => {
      try {
        const res = await fetchEventList({ page: 1, limit: 1, status });
        return res?.pagination?.total ?? null;
      } catch {
        return null;
      }
    };

    const [total, running, comingSoon, completed, draft] = await Promise.all([
      countOf("all"),
      countOf("running"),
      countOf("coming_soon"),
      countOf("completed"),
      countOf("draft"),
    ]);
    setStats({ total, running, coming_soon: comingSoon, completed, draft });
  }, []);

  // Load events
  const loadEvents = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchEventList({
        page: currentPage,
        limit: 15,
        status: statusFilter,
        search: debouncedSearch,
      });

      if (res && res.status) {
        setEvents(res.data || []);
        if (res.pagination) {
          setCurrentPage(res.pagination.current_page || 1);
          setTotalPages(res.pagination.total_pages || 1);
          setTotalCount(res.pagination.total || 0);
        }
      } else {
        toast.error(res?.message || "Failed to load events");
      }
    } catch (err) {
      toast.error(err.message || "Failed to load events");
    } finally {
      setLoading(false);
    }
  }, [currentPage, statusFilter, debouncedSearch]);

  useScrollRestoration(loading, "eventsList_scroll");

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  // Debounced search
  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    setCurrentPage(1);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setDebouncedSearch(val);
    }, 400);
  };

  const handleStatusChange = (newStatus) => {
    setStatusFilter(newStatus);
    setCurrentPage(1);
  };

  const changeEventStatus = async (eventItem, status, confirm = false) => {
    try {
      const res = await updateEventStatus(eventItem.id, { status, confirm });
      toast.success(res.message || "Event status updated successfully");
      loadEvents();
      loadStats();
    } catch (err) {
      toast.error(err.message || "Failed to update event status");
    }
  };

  const handleEventStatusChange = (eventItem, status) => {
    if (status === eventItem.status) return;

    const destructive =
      eventItem.status === "completed" ||
      (eventItem.status === "running" && ["draft", "coming_soon", "cancelled"].includes(status));

    if (!destructive) {
      changeEventStatus(eventItem, status);
      return;
    }

    setConfirmModal({
      isOpen: true,
      title: "Change Event Status",
      message: `Change "${eventItem.name}" from ${getStatusLabel(eventItem.status)} to ${getStatusLabel(status)}?`,
      subMessage: "This can affect the event lifecycle and may not be reversible.",
      confirmText: "Change Status",
      confirmColor: "blue",
      icon: FiRefreshCw,
      action: async () => {
        try {
          setConfirmModal((prev) => ({ ...prev, loading: true }));
          await changeEventStatus(eventItem, status, true);
          setConfirmModal((prev) => ({ ...prev, isOpen: false }));
        } finally {
          setConfirmModal((prev) => ({ ...prev, loading: false }));
        }
      },
    });
  };

  // Publish Event
  /**
   * Finalize — the other half of a status change.
   *
   * The API has no generic "set status" endpoint: publish and finalize are the
   * only two transitions an admin can make, so those are the two offered here.
   * `coming_soon` opens on its own and `cancelled` is unreachable from the API.
   */
  const handleFinalizeClick = (eventItem) => {
    setConfirmModal({
      isOpen: true,
      title: "Finalize Event",
      message: `Finalize "${eventItem.name}" and close it?`,
      subMessage:
        "This freezes the final leaderboard, credits every PX prize automatically, and builds the payout queue for the coins a person sends by hand. It cannot be undone.",
      confirmText: "Finalize Now",
      confirmColor: "blue",
      icon: FiCheckCircle,
      action: async () => {
        try {
          setConfirmModal((prev) => ({ ...prev, loading: true }));
          const res = await finalizeEvent(eventItem.id);
          toast.success(res.message || "Event finalized");
          setConfirmModal((prev) => ({ ...prev, isOpen: false }));
          loadEvents();
          loadStats();
        } catch (err) {
          toast.error(err.message || "Failed to finalize event");
        } finally {
          setConfirmModal((prev) => ({ ...prev, loading: false }));
        }
      },
    });
  };

  const handlePublishClick = (eventItem) => {
    setConfirmModal({
      isOpen: true,
      title: "Publish Event",
      message: `Are you sure you want to publish "${eventItem.name}"?`,
      subMessage:
        "Publishing will make this event LIVE and active for all users immediately. Only one event can run at a time.",
      confirmText: "Publish Now",
      confirmColor: "emerald",
      icon: FiPlay,
      action: async () => {
        try {
          setConfirmModal((prev) => ({ ...prev, loading: true }));
          const res = await publishEvent(eventItem.id, eventItem.notify_on_publish);
          toast.success(res.message || "Event published successfully!");
          setConfirmModal((prev) => ({ ...prev, isOpen: false }));
          loadEvents();
        } catch (err) {
          toast.error(err.message || "Failed to publish event");
        } finally {
          setConfirmModal((prev) => ({ ...prev, loading: false }));
        }
      },
    });
  };

  // Delete Event
  const handleDeleteClick = (eventItem) => {
    setConfirmModal({
      isOpen: true,
      title: "Delete Event",
      message: `Are you sure you want to delete "${eventItem.name}" (${eventItem.event_code})?`,
      subMessage: "This action cannot be undone. Only draft events can be deleted.",
      confirmText: "Delete Event",
      confirmColor: "red",
      icon: FiTrash2,
      action: async () => {
        try {
          setConfirmModal((prev) => ({ ...prev, loading: true }));
          const res = await deleteEvent(eventItem.id);
          toast.success(res.message || "Event deleted successfully!");
          setConfirmModal((prev) => ({ ...prev, isOpen: false }));
          loadEvents();
        } catch (err) {
          toast.error(err.message || "Failed to delete event");
        } finally {
          setConfirmModal((prev) => ({ ...prev, loading: false }));
        }
      },
    });
  };

  /**
   * A schedule boundary, with its time.
   *
   * UTC, because that is the zone the schedule is stored and judged in — a
   * local rendering flips the day either side of midnight UTC. The time is
   * shown because an event that ends at 09:00 and one that ends at 23:59 are
   * a working day apart, and the date alone hid that.
   */
  const formatDateTime = (dateStr) => {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d
      .toLocaleString("en-GB", {
        timeZone: "UTC",
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      })
      .replace(",", "");
  };

  return (
    <div className="flex h-screen bg-gray-50/50">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <Navbar />

        <main className="flex-1 overflow-y-auto px-4 sm:px-8 py-6">
          <div className="max-w-7xl mx-auto space-y-6">
            {/* Header section */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h1 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                  <FiGift className="text-blue-600" /> Event Management
                </h1>
                <p className="text-xs text-gray-400 mt-1">
                  Manage mining bonanzas, leaderboard ladders, sponsors, and participant rewards
                </p>
              </div>

              <div className="flex items-center gap-3 flex-wrap">
                <UtcClock />

                <button
                  onClick={() => {
                    loadEvents();
                    loadStats();
                  }}
                  disabled={loading}
                  title="Refresh list"
                  className="p-2.5 bg-white border border-gray-200 hover:bg-gray-50 text-gray-600 rounded-xl transition shadow-sm"
                >
                  <FiRefreshCw size={15} className={loading ? "animate-spin text-blue-600" : ""} />
                </button>
                <button
                  onClick={() => navigate("/event/create")}
                  className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-xl transition shadow-sm shadow-blue-600/20 flex items-center gap-2"
                >
                  <FiPlus size={16} /> Create Event
                </button>
              </div>
            </div>

            {/* Counters — each is the API's own total for that filter */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { key: "total", label: "Total Events", value: stats.total, icon: FiGift, tone: "text-gray-800", bg: "bg-gray-100 text-gray-600" },
                { key: "running", label: "Active (Running)", value: stats.running, icon: FiPlay, tone: "text-emerald-600", bg: "bg-emerald-50 text-emerald-600" },
                { key: "completed", label: "Completed", value: stats.completed, icon: FiAward, tone: "text-blue-600", bg: "bg-blue-50 text-blue-600" },
                { key: "coming_soon", label: "Coming Soon", value: stats.coming_soon, icon: FiClock, tone: "text-violet-600", bg: "bg-violet-50 text-violet-600" },
              ].map((card) => {
                const Icon = card.icon;
                const isActiveFilter = statusFilter === (card.key === "total" ? "all" : card.key);

                return (
                  <button
                    key={card.key}
                    onClick={() => handleStatusChange(card.key === "total" ? "all" : card.key)}
                    className={`p-4 bg-white rounded-2xl border shadow-sm text-left transition hover:border-gray-300 ${
                      isActiveFilter ? "border-blue-400 ring-2 ring-blue-500/10" : "border-gray-100"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider truncate">
                          {card.label}
                        </p>
                        <p className={`text-2xl font-bold mt-1 ${card.tone}`}>
                          {/* `== null` on purpose: it catches undefined too,
                              so a count that never arrived shows a dash
                              rather than taking the page down. */}
                          {card.value == null ? (
                            <span className="text-gray-300 text-base">—</span>
                          ) : (
                            Number(card.value).toLocaleString()
                          )}
                        </p>
                      </div>
                      <span className={`w-9 h-9 shrink-0 rounded-xl flex items-center justify-center ${card.bg}`}>
                        <Icon size={16} />
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Filter Tabs & Search Bar */}
            <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 flex flex-col md:flex-row md:items-center justify-between gap-4">
              {/* Status Filter Tabs */}
              <div className="flex items-center gap-1 overflow-x-auto pb-1 md:pb-0 scrollbar-none">
                {STATUS_TABS.map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => handleStatusChange(tab.key)}
                    className={`px-3.5 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition ${
                      statusFilter === tab.key
                        ? "bg-blue-600 text-white shadow-sm shadow-blue-600/20"
                        : "text-gray-500 hover:text-gray-800 hover:bg-gray-100"
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Search input */}
              <div className="relative w-full md:w-72">
                <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={15} />
                <input
                  type="text"
                  placeholder="Search by name or event code..."
                  value={search}
                  onChange={handleSearchChange}
                  className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-xl text-xs focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition"
                />
              </div>
            </div>

            {/* Events Table Container */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-gray-50/60 border-b border-gray-100 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                      <th className="px-6 py-3.5">Event Info</th>
                      <th className="px-6 py-3.5">Status</th>
                      <th className="px-6 py-3.5">Schedule</th>
                      <th className="px-6 py-3.5">Rewards & Rules</th>
                      <th className="px-6 py-3.5">Participants</th>
                      <th className="px-6 py-3.5 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 text-xs">
                    {loading && events.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-6 py-12 text-center text-gray-400">
                          <div className="flex flex-col items-center gap-2">
                            <FiRefreshCw className="animate-spin text-blue-600" size={20} />
                            <span>Loading events...</span>
                          </div>
                        </td>
                      </tr>
                    ) : events.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-6 py-12 text-center text-gray-400">
                          <div className="flex flex-col items-center gap-2">
                            <FiGift size={28} className="text-gray-300" />
                            <p className="font-medium text-gray-500">No events found</p>
                            <p className="text-xs text-gray-400">
                              {debouncedSearch ? "Try adjusting your search query" : "Click '+ Create Event' to launch a new event."}
                            </p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      events.map((event) => (
                        <tr
                          key={event.id}
                          className="hover:bg-gray-50/70 transition cursor-pointer"
                          onClick={() => navigate(`/event/${event.id}`)}
                        >
                          {/* Event Name & Code */}
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center font-bold text-sm shrink-0">
                                {event.name?.charAt(0)?.toUpperCase() || "E"}
                              </div>
                              <div>
                                <p className="font-semibold text-gray-800 text-sm hover:text-blue-600 transition">
                                  {event.name}
                                </p>
                                <p className="text-[11px] text-gray-400 font-mono mt-0.5">
                                  {event.event_code}
                                </p>
                              </div>
                            </div>
                          </td>

                          {/* Status */}
                          <td className="px-6 py-4">
                            <select
                              value={event.status || "draft"}
                              onChange={(e) => {
                                e.stopPropagation();
                                handleEventStatusChange(event, e.target.value);
                              }}
                              onClick={(e) => e.stopPropagation()}
                              title="Change event status"
                              className={`px-2.5 py-1 rounded-full text-[11px] font-medium border bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 ${getStatusBadgeClass(
                                event.status
                              )}`}
                            >
                              {STATUS_TABS.filter((tab) => tab.key !== "all").map((tab) => (
                                <option key={tab.key} value={tab.key}>
                                  {tab.label}
                                </option>
                              ))}
                            </select>
                            {event.current_day_number && event.total_days && (
                              <p className="text-[10px] text-gray-400 mt-0.5">
                                Day {event.current_day_number} of {event.total_days}
                              </p>
                            )}
                          </td>

                          {/* Schedule */}
                          <td className="px-6 py-4">
                            {(() => {
                              const note = scheduleNote(event, now);
                              return (
                                <div className="space-y-0.5">
                                  <p className="flex items-center gap-1.5 text-gray-700 font-medium whitespace-nowrap">
                                    <FiClock className="text-emerald-500 shrink-0" size={12} />
                                    {formatDateTime(event.start_date)}
                                    <span className="text-gray-400 font-normal">UTC</span>
                                  </p>
                                  <p className="text-[11px] text-gray-400 whitespace-nowrap pl-[18px]">
                                    to {formatDateTime(event.end_date)} UTC
                                  </p>
                                  {note && (
                                    <span
                                      className={`inline-flex items-center mt-1 px-2 py-0.5 rounded-full border text-[10px] font-semibold ${NOTE_TONES[note.tone]}`}
                                    >
                                      {note.text}
                                    </span>
                                  )}
                                </div>
                              );
                            })()}
                          </td>

                          {/* Rewards & Rules */}
                          <td className="px-6 py-4">
                            <div className="space-y-0.5">
                              <p className="text-gray-700 font-medium flex items-center gap-1.5">
                                <FiAward className="text-amber-500" size={13} /> Top {event.prize_top_n || 1000}
                              </p>
                              <p className="text-[11px] text-gray-400">
                                Check-in: +{event.daily_check_in_reward ?? 1} pts
                                {event.check_in_credits_coin ? " (Coins On)" : ""}
                              </p>
                            </div>
                          </td>

                          {/* Participants */}
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-1.5 text-gray-700 font-medium">
                              <FiUsers className="text-gray-400" size={14} />
                              {/* The live count first: `total_participants` is
                                  frozen at finalize, so it is null for every
                                  running event and reading it first showed a
                                  zero next to an event people had joined. */}
                              <span>{event.participant_count ?? event.total_participants ?? 0}</span>
                            </div>
                          </td>

                          {/* Actions */}
                          <td className="px-6 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center justify-end gap-1">
                              {/* View / Manage */}
                              <button
                                onClick={() => navigate(`/event/${event.id}`)}
                                title="Manage Event"
                                className="p-1.5 rounded-lg text-gray-500 hover:bg-blue-50 hover:text-blue-600 transition"
                              >
                                <FiEye size={16} />
                              </button>

                              {/* Participants — straight to the tab rather
                                  than the overview with a click still to go */}
                              <button
                                onClick={() => navigate(`/event/${event.id}?tab=participants`)}
                                title="View participants"
                                className="p-1.5 rounded-lg text-gray-500 hover:bg-indigo-50 hover:text-indigo-600 transition"
                              >
                                <FiUsers size={16} />
                              </button>

                              {/* Finalize (if running) */}
                              {event.status === "running" && (
                                <button
                                  onClick={() => handleFinalizeClick(event)}
                                  title="Finalize (close and pay out)"
                                  className="p-1.5 rounded-lg text-blue-600 hover:bg-blue-50 transition"
                                >
                                  <FiCheckCircle size={16} />
                                </button>
                              )}

                              {/* Publish (if draft) */}
                              {event.status === "draft" && (
                                <button
                                  onClick={() => handlePublishClick(event)}
                                  title="Publish (Make Live)"
                                  className="p-1.5 rounded-lg text-emerald-600 hover:bg-emerald-50 transition"
                                >
                                  <FiPlay size={16} />
                                </button>
                              )}

                              {/* Edit (if not completed) */}
                              {event.status !== "completed" && (
                                <button
                                  onClick={() => navigate(`/event/${event.id}/edit`)}
                                  title="Edit Event"
                                  className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition"
                                >
                                  <FiEdit2 size={16} />
                                </button>
                              )}

                              {/* Delete — drafts only, and it says why when
                                  it is not. A running or completed event has
                                  user scores and a payout record hanging off
                                  it, so the API refuses. */}
                              <button
                                onClick={() => handleDeleteClick(event)}
                                disabled={event.status !== "draft"}
                                title={
                                  event.status === "draft"
                                    ? "Delete event"
                                    : `Only a draft can be deleted — this one is ${getStatusLabel(event.status)}`
                                }
                                className={`p-1.5 rounded-lg transition ${
                                  event.status === "draft"
                                    ? "text-rose-500 hover:bg-rose-50 hover:text-rose-700"
                                    : "text-gray-200 cursor-not-allowed"
                                }`}
                              >
                                <FiTrash2 size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="px-6 py-3.5 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
                  <span>
                    Showing {events.length} of {totalCount} events
                  </span>
                  <div className="flex items-center gap-1.5">
                    <button
                      disabled={currentPage <= 1 || loading}
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      className="px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                    >
                      Previous
                    </button>
                    <span className="px-2 font-medium">
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      disabled={currentPage >= totalPages || loading}
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      className="px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>

      {/* Confirmation Modal */}
      <ConfirmActionModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        subMessage={confirmModal.subMessage}
        confirmText={confirmModal.confirmText}
        confirmColor={confirmModal.confirmColor}
        icon={confirmModal.icon}
        loading={confirmModal.loading}
        onClose={() => setConfirmModal((prev) => ({ ...prev, isOpen: false }))}
        onConfirm={confirmModal.action}
      />
    </div>
  );
};

export default EventsList;
